import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../../utils/store';
import { useEffect, useState } from 'react';
import HttpService from '../../../services/HttpService';
import Constants from '../../../utils/constants';
import FormFieldData from '../../../Models/FormFieldData';
import { Stack } from '@mui/material';
import CommonForm from '../../../components/form/CommonForm';
import AlertMessageComponent from '../../../components/AlertMessageComponent';
import { useAlert } from '../../../components/alertContext';
import { handleErrorMessage } from '../../../utils/storageUtil';

const CustomerForm: React.FC = () => {
  const { getAdmin } = useStore();
  const { currentId } = useParams<{ currentId: any }>();
  const navigate = useNavigate();

  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);

  type customerFormObj = {
    [key: string]: {
      required: boolean;
      value: null | string;
    };
  };
  const [customerForm, setCustomerForm] = useState<customerFormObj>({
    id: { required: false, value: null },
    name: { required: true, value: '' },
    tradingName: { required: true, value: '' },
    abn: { required: true, value: '' },
    address: { required: true, value: '' },
    email: { required: true, value: '' },
    phone: { required: true, value: '' },

    creditLimit: { required: false, value: '' },
    notes: { required: false, value: '' },
  });

  useEffect(() => {
    if (currentId) {
      if (currentId !== 'Add') {
        fetchSingleRecord(currentId);
      }
    } else {
      setCurrentRecord(null);
    }
  }, [currentId]);

  const fetchSingleRecord = async (id: any) => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.customersURL + '/' + id,
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        null
      );
      if (response.success) {
        handleRowClick(response.data);
      }

      console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`PATCH Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  const handleCustomerFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setCustomerForm((prev) => ({
      ...prev,
      [name]: { required: prev[name].required, value: value },
    }));
  };

  const clearForm = () => {
    setCustomerForm({
      id: { required: false, value: null },
      name: { required: true, value: '' },
      tradingName: { required: true, value: '' },
      abn: { required: true, value: '' },
      address: { required: true, value: '' },
      email: { required: true, value: '' },
      phone: { required: true, value: '' },

      creditLimit: { required: false, value: '' },
      notes: { required: false, value: '' },
    });
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    setCurrentRecord(null);
    navigate('/data-management/customer');
  };

  const handleSave = async () => {
    interface customerRequest {
      id: string | null;
      name: string | null;
      tradingName: string | null;
      abn: string | null;
      address: string | null;
      email: string | null;
      phone: string | null;
      customerID: string | null;
      creditLimit: string | null;
      notes: string | null;
    }

    const customerReq: customerRequest = {
      id: customerForm.id.value,
      name: customerForm.name.value,
      tradingName: customerForm.tradingName.value,
      abn: customerForm.abn.value,
      address: customerForm.address.value,
      email: customerForm.email.value,
      phone: customerForm.phone.value,
      customerID: 'blank',
      creditLimit: customerForm.creditLimit.value,
      notes: customerForm.creditLimit.value,
    };

    setLoading(true);
    const endpoint = Constants.apiURL + Constants.customersURL;
    if (currentRecord) {
      customerForm['id'] = currentRecord.id;
    }
    let response: any;

    try {
      if (currentRecord) {
        response = await HttpService.put(endpoint, customerReq, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      } else {
        response = await HttpService.post(endpoint, customerReq, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      }
      if (response.success) {
        clearForm();
      }
    } catch (error: any) {
      console.error(`PATCH Error:`, error);
      setAlert({
        type: 'error',
        message: handleErrorMessage(error),
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (record: any) => {
    setCurrentRecord(record);
    setCustomerForm({
      id: { required: false, value: record.id },
      name: { required: true, value: record.name },
      tradingName: { required: true, value: record.tradingName },
      abn: { required: true, value: record.abn },
      address: { required: true, value: record.address },
      email: { required: true, value: record.email },
      phone: { required: true, value: record.phone },

      creditLimit: { required: false, value: record.creditLimit },
      notes: { required: false, value: record.notes },
    });
  };

  const abnRegex = /^\d{11}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex =
    /^(\+?\(61\)|\(\+?61\)|\+?61|\(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$/;

  const formFields: FormFieldData[] = [
    {
      name: 'name',
      label: 'Customer Name',
      value: customerForm.name.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.name.required,
      size: 12,
      type: 'textfield',
      validationRules: [(value) => (!value ? 'Customer name is required' : '')],
    },
    {
      name: 'tradingName',
      label: 'Trading Name',
      value: customerForm.tradingName.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.tradingName.required,
      size: 6,
      type: 'textfield',
      validationRules: [(value) => (!value ? 'Trading name is required' : '')],
    },
    {
      name: 'abn',
      label: 'ABN Number',
      value: customerForm.abn.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.abn.required,
      size: 6,
      type: 'textfield',
      inputType: 'number',
      validationRules: [
        (value) => (!value ? 'ABN Number is required' : ''),
        (value) =>
          !abnRegex.test(value) ? 'Invalid ABN. Must be 11 digits' : '',
      ],
    },
    {
      name: 'address',
      label: 'Customer Address',
      value: customerForm.address.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.address.required,
      size: 12,
      type: 'textfield',
      validationRules: [(value) => (!value ? 'Address is required' : '')],
    },
    {
      name: 'email',
      label: 'Email Address',
      value: customerForm.email.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.email.required,
      size: 6,
      type: 'textfield',
      inputType: 'email',
      validationRules: [
        (value) => (!value ? 'Email address is required' : ''),
        (value) => (!emailRegex.test(value) ? 'Invalid email address' : ''),
      ],
    },
    {
      name: 'phone',
      label: 'Phone Number',
      value: customerForm.phone.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.phone.required,
      size: 6,
      type: 'textfield',
      inputType: 'tel',
      validationRules: [
        (value) => (!value ? 'Phone number is required' : ''),
        (value) =>
          !phoneRegex.test(value) ? 'Invalid Australian phone number' : '',
      ],
    },

    {
      name: 'creditLimit',
      label: 'Credit Limit',
      value: customerForm.creditLimit.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.creditLimit.required,
      size: 12,
      type: 'textfield',
      inputType: 'number',
      validationRules: [],
    },
    {
      name: 'notes',
      label: 'Notes',
      value: customerForm.notes.value,
      onChange: handleCustomerFormChange,
      isRequired: customerForm.notes.required,
      size: 12,
      type: 'textareaField',
      validationRules: [],
      keyname: 'txtareanotes',
      minRows: 7,
    },
  ];
  return (
    <Stack>
      <CommonForm
        fields={formFields}
        onCancel={() => {}}
        formReset={clearForm}
        formTitle={currentId !== 'Add' ? 'Update Customer' : 'Add New Customer'}
        isLoading={loading}
        isUpdate={currentId !== 'Add' ? true : false}
        onSave={handleSave}
        actionButtonPosition="bottom"
      />
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default CustomerForm;
